import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronRight, faChevronLeft } from '@/icons';
import { CustomTooltip } from './CustomTooltip';

interface TutorialOverlayProps {
    isVisible: boolean;
    targetElement: HTMLElement | null;
    title: string;
    description: string;
    onClose: () => void;
    onNext?: () => void;
    onPrevious?: () => void;
    onSkip?: () => void;
    showNext?: boolean;
    showPrevious?: boolean;
    currentStep?: number;
    totalSteps?: number;
    position?: 'top' | 'bottom' | 'left' | 'right';
}

export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
    isVisible,
    targetElement,
    title,
    description,
    onClose,
    onNext,
    onPrevious,
    onSkip,
    showNext = false,
    showPrevious = false,
    currentStep = 1,
    totalSteps = 1,
    position = 'bottom'
}) => {
    const theme = useTheme();
    const [isSkipHovered, setIsSkipHovered] = useState(false);

    if (!isVisible || !targetElement) return null;

    // Get target element position
    const rect = targetElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    const targetTop = rect.top + scrollTop;
    const targetLeft = rect.left + scrollLeft;
    const targetWidth = rect.width;
    const targetHeight = rect.height;

    // Calculate tooltip position based on element position and screen boundaries
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 250; // Approximate tooltip width
    const tooltipHeight = 120; // Approximate tooltip height
    const margin = 60;

    // Determine best position based on element location
    let finalPosition = position;
    let tooltipTop = 0;
    let tooltipLeft = 0;
    let arrowPosition = '';

    // Auto-detect best position if not specified
    if (position === 'bottom') {
        // Check if there's enough space below
        const spaceBelow = viewportHeight - (targetTop + targetHeight + margin + tooltipHeight);
        const spaceAbove = targetTop - margin - tooltipHeight;
        const spaceRight = viewportWidth - (targetLeft + targetWidth + margin + tooltipWidth);
        const spaceLeft = targetLeft - margin - tooltipWidth;

        if (spaceBelow < 0 && spaceAbove > spaceBelow) {
            finalPosition = 'top';
        } else if (spaceRight < 0 && spaceLeft > spaceRight) {
            finalPosition = 'left';
        } else if (spaceLeft < 0 && spaceRight > spaceLeft) {
            finalPosition = 'right';
        }
    }

    switch (finalPosition) {
        case 'top':
            tooltipTop = rect.top - tooltipHeight - margin;
            tooltipLeft = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            arrowPosition = 'bottom';
            break;
        case 'bottom':
            tooltipTop = rect.bottom + margin - 45; // Move up 45px (down 5px from previous)
            tooltipLeft = rect.left + (rect.width / 2) - (tooltipWidth / 2) - 40; // Move left 40px
            arrowPosition = 'top';
            break;
        case 'left':
            tooltipTop = rect.top + (rect.height / 2) - (tooltipHeight / 2) - 40; // Move up 40px
            tooltipLeft = rect.left - tooltipWidth - margin - 20; // Move further left 20px
            arrowPosition = 'right';
            break;
        case 'right':
            tooltipTop = rect.top + (rect.height / 2) - (tooltipHeight / 2) - 40; // Move up 40px
            tooltipLeft = rect.right + margin - 45; // Move left 45px (5px more)
            arrowPosition = 'left';
            break;
    }

    // Clamp tooltip to viewport boundaries
    tooltipTop = Math.max(margin, Math.min(tooltipTop, viewportHeight - tooltipHeight - margin));
    tooltipLeft = Math.max(margin, Math.min(tooltipLeft, viewportWidth - tooltipWidth - margin));

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
        },
        highlight: {
            position: 'absolute' as const,
            top: targetTop,
            left: targetLeft,
            width: targetWidth,
            height: targetHeight,
            border: `3px solid ${theme.colors.primary}`,
            borderRadius: theme.borderRadius.sm,
            boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.5)`,
            zIndex: 10000,
        },
        tooltip: {
            position: 'absolute' as const,
            top: tooltipTop,
            left: tooltipLeft,
            backgroundColor: theme.colors.surface,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.md,
            padding: theme.spacing.lg,
            boxShadow: theme.shadows.lg,
            maxWidth: '320px',
            zIndex: 10001,
        },
        tooltipArrow: {
            position: 'absolute' as const,
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            ...(arrowPosition === 'top' && {
                top: '-16px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderBottomColor: theme.colors.surface,
            }),
            ...(arrowPosition === 'bottom' && {
                top: '-16px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderBottomColor: theme.colors.surface,
            }),
            ...(arrowPosition === 'left' && {
                left: '-16px',
                top: '50%',
                transform: 'translateY(-50%)',
                borderRightColor: theme.colors.surface,
            }),
            ...(arrowPosition === 'right' && {
                right: '-16px',
                top: '50%',
                transform: 'translateY(-50%)',
                borderLeftColor: theme.colors.surface,
            }),
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.sm,
        },
        title: {
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text.primary,
            margin: 0,
        },
        closeButton: {
            background: 'none',
            border: 'none',
            color: theme.colors.text.secondary,
            cursor: 'pointer',
            padding: theme.spacing.xs,
            borderRadius: theme.borderRadius.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        description: {
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.text.primary,
            lineHeight: 1.5,
            marginBottom: theme.spacing.md,
        },
        footer: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.spacing.sm,
        },
        button: {
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            border: 'none',
            borderRadius: theme.borderRadius.sm,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.xs,
            transition: `all ${theme.transitions.normal}`,
        },
        closeButtonText: {
            backgroundColor: 'transparent',
            color: theme.colors.text.secondary,
            border: `1px solid ${theme.colors.border.default}`,
        },
        tooltipContent: {
            position: 'relative' as const,
            zIndex: 10002,
        },
        tooltipHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.sm,
        },
        tooltipTitle: {
            fontSize: theme.typography.fontSize.md,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text.primary,
        },
        tooltipCloseButton: {
            background: 'none',
            border: 'none',
            color: theme.colors.text.secondary,
            cursor: 'pointer',
            padding: theme.spacing.xs,
            borderRadius: theme.borderRadius.sm,
            fontSize: theme.typography.fontSize.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
        },
        tooltipDescription: {
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.text.primary,
            lineHeight: 1.4,
        },
        tooltipFooter: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: theme.spacing.md,
        },
        navigationButton: {
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            border: 'none',
            borderRadius: theme.borderRadius.sm,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            transition: `all ${theme.transitions.normal}`,
        },
        previousButton: {
            backgroundColor: theme.colors.surfaceSecondary,
            color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.border.default}`,
        },
        nextButton: {
            backgroundColor: theme.colors.primary,
            color: theme.colors.text.inverse,
        },
        stepIndicator: {
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.text.secondary,
        },
        skipButton: {
            backgroundColor: theme.colors.surfaceSecondary,
            color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.sm,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            transition: `all ${theme.transitions.normal}`,
        },
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.highlight} />
            <div style={styles.tooltip}>
                <div style={styles.tooltipArrow} />
                <div style={styles.tooltipContent}>
                    <div style={styles.tooltipHeader}>
                        <span style={styles.tooltipTitle}>{title}</span>
                        <CustomTooltip text="Close tutorial">
                        <button
                            style={styles.tooltipCloseButton}
                            onClick={onClose}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        </CustomTooltip>
                    </div>
                    <div style={styles.tooltipDescription}>
                        {description}
                    </div>
                    {(showNext || showPrevious) && (
                        <div style={styles.tooltipFooter}>
                            <button
                                style={{
                                    ...styles.navigationButton,
                                    ...styles.previousButton,
                                    opacity: currentStep <= 1 ? 0.5 : 1,
                                    cursor: currentStep <= 1 ? 'not-allowed' : 'pointer',
                                }}
                                onClick={currentStep > 1 ? onPrevious : undefined}
                                disabled={currentStep <= 1}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <span style={styles.stepIndicator}>
                                Step {currentStep} of {totalSteps}
                            </span>
                            {showNext && onNext && (
                                <button
                                    style={{
                                        ...styles.navigationButton,
                                        ...styles.nextButton,
                                        opacity: currentStep >= totalSteps ? 0.5 : 1,
                                        cursor: currentStep >= totalSteps ? 'not-allowed' : 'pointer',
                                    }}
                                    onClick={currentStep < totalSteps ? onNext : undefined}
                                    disabled={currentStep >= totalSteps}
                                >
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            )}
                        </div>
                    )}
                    {onSkip && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: theme.spacing.sm,
                        }}>
                            <button
                                style={{
                                    ...styles.skipButton,
                                    backgroundColor: isSkipHovered ? theme.colors.surface : theme.colors.surfaceSecondary,
                                    borderColor: isSkipHovered ? theme.colors.primary : theme.colors.border.default,
                                }}
                                onClick={onSkip}
                                onMouseEnter={() => setIsSkipHovered(true)}
                                onMouseLeave={() => setIsSkipHovered(false)}
                            >
                                {currentStep >= totalSteps ? 'Done' : 'Skip tutorial'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
